import type { DayData } from '@tix/ds/components/organisms/calendar/types.ts'
import { TixDate } from '@tix/tix-time'

type ApiDate = { date?: string; status?: string }

/**
 * Converts the minimal API date objects from `action_prompt_pick_date` into the rich `DayData`
 * format that the DS CalendarPicker expects.
 *
 * **API limitation**: The pick_date action only provides `date` and `status` per entry.
 * Full calendar features would additionally require:
 *   - `price` / `pricePrefix` — for displaying per-day pricing
 *   - `available` (remaining capacity) — for low-availability warnings
 *   - Annotation/message data — for promotional or informational badges/dots
 */
export function apiDatesToDayData(apiDates: ApiDate[], timezone: string): DayData[] {
  const today = TixDate.today(timezone)

  return apiDates
    .filter((d): d is Required<ApiDate> => Boolean(d.date && d.status))
    .map((d) => {
      const tixDate = new TixDate(d.date)
      const isAvailable = d.status === 'available'
      const isSoldOut = d.status === 'sold_out'
      const status: DayData['status'] = isAvailable ? 'available' : isSoldOut ? 'sold_out' : 'unavailable'
      // TODO Should className be a computed property in CalendarDay instead?
      const className = status.replace('_', '-')
      const statusLabel = isAvailable ? '' : isSoldOut ? ' (sold out)' : ' (unavailable)'

      return {
        date: tixDate,
        status,
        enabled: isAvailable,
        annotations: [],
        dots: [],
        className,
        tooltip: tixDate.formatTodayTomorrow(today),
        longFormat: tixDate.format('dddd, MMMM D, YYYY'),
        ariaLabel: `${tixDate.format('MMMM D, YYYY')}${statusLabel}`,
      } satisfies DayData
    })
}
